import React, { useCallback, useEffect, useState } from 'react';
import {
  getExternalAppCalibration,
  listExternalAppContentTargets,
  sendExternalAppCalibrationAction,
  startExternalAppCalibration,
} from './externalAppControlApi';
import LaunchRecipePanel from './LaunchRecipePanel';
import PlaybackHealthPanel from './PlaybackHealthPanel';
import PromptDetectionPanel from './PromptDetectionPanel';
import PlaybookSetupPanel from './PlaybookSetupPanel';

const GOOGLE_REMOTE_ACTIONS = [
  'home',
  'back',
  'up',
  'down',
  'left',
  'right',
  'enter',
  'play',
  'pause',
  'play_pause',
];

const SETUP_REMOTE_BUTTONS = [
  ['home', 'Home'],
  ['up', '↑'],
  ['back', 'Back'],
  ['left', '←'],
  ['enter', 'OK'],
  ['right', '→'],
  ['down', '↓'],
];

function ModernAppSetup({
  app,
  profile,
  deviceId,
  onClose,
  onLaunchApp,
  onLaunchSuitestream,
  onRemoteAction,
  controlRoute = 'google_tv',
  accessibilityEnabled = false,
}) {
  const label = app?.app_name || app?.package_name || 'Unknown App';
  const [session, setSession] = useState(null);
  const [targets, setTargets] = useState([]);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [rulesVersion, setRulesVersion] = useState(0);
  const [captureRequest, setCaptureRequest] = useState(0);
  const [controlTransport, setControlTransport] = useState(
    controlRoute === 'google_tv' ? 'google_tv' : 'accessibility'
  );

  useEffect(() => {
    setControlTransport(controlRoute === 'google_tv' ? 'google_tv' : 'accessibility');
  }, [controlRoute]);

  const dispatchRemoteAction = async (action) => {
    if (controlTransport === 'google_tv') return onRemoteAction?.(action);
    if (!session?.id) throw new Error('Calibration session is still loading');
    return sendExternalAppCalibrationAction(session.id, action, 'accessibility');
  };

  const load = useCallback(async () => {
    if (!profile?.id) {
      setStatus('error');
      setError('This app profile is not ready yet. Refresh the app list and try again.');
      return;
    }
    setStatus('loading');
    setError('');
    try {
      const [nextSession, allTargets] = await Promise.all([
        startExternalAppCalibration(profile.id),
        listExternalAppContentTargets(deviceId),
      ]);
      setSession(nextSession);
      setTargets(allTargets.filter((target) => target.profileId === profile.id));
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.error || err.message || 'App launch settings could not be loaded.');
    }
  }, [deviceId, profile?.id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="apps-modern-setup" aria-label={`Configure ${label}`}>
      <div className="apps-modern-setup__header">
        <div>
          <span className="apps-setup-eyebrow">App Launch Settings</span>
          <h3>{label}</h3>
          <p>
            Train app states and routes with the preferred Google TV connection, or deliberately
            select Accessibility when validating the fallback route.
          </p>
        </div>
        <button type="button" onClick={onClose}>Close</button>
      </div>

      <div className="apps-modern-setup__route">
        <label htmlFor={`app-control-transport-${profile?.id}`}>Training control transport</label>
        <select
          id={`app-control-transport-${profile?.id}`}
          value={controlTransport}
          onChange={(event) => setControlTransport(event.target.value)}
        >
          <option value="google_tv" disabled={controlRoute !== 'google_tv'}>
            Google TV Remote{controlRoute === 'google_tv' ? ' · preferred' : ' · not paired'}
          </option>
          <option value="accessibility" disabled={!accessibilityEnabled}>
            Accessibility{accessibilityEnabled ? ' · fallback' : ' · not enabled'}
          </option>
        </select>
      </div>

      <section className="apps-setup-remote" aria-label="Live device controls">
        <div className="apps-setup-remote__copy">
          <strong>Live device controls</strong>
          <span>
            Move around the TV while identifying ready screens and startup obstacles. These buttons
            control the device without adding steps to an obstacle response.
          </span>
        </div>
        <div className="apps-setup-remote__launchers">
          <button type="button" className="primary" onClick={onLaunchApp}>
            Launch {label}
          </button>
          <button type="button" className="secondary" onClick={onLaunchSuitestream}>
            Launch Suitestream
          </button>
        </div>
        <div className="apps-setup-remote__dpad">
          {SETUP_REMOTE_BUTTONS.map(([action, buttonLabel]) => (
            <button
              key={action}
              type="button"
              className={`apps-setup-remote__${action}`}
              onClick={() => dispatchRemoteAction(action).catch((err) => {
                setError(err.response?.data?.message || err.message || 'Remote action failed.');
              })}
              aria-label={buttonLabel === 'OK' ? 'Select' : buttonLabel}
            >
              {buttonLabel}
            </button>
          ))}
        </div>
      </section>

      {status === 'loading' && <div className="dashboard-app-launcher-status">Loading launch settings…</div>}
      {status === 'error' && (
        <div className="dashboard-app-launcher-status error">
          {error}
          <button type="button" onClick={load}>Retry</button>
        </div>
      )}
      {session && (
        <>
          <section className="apps-configurator-section">
            <div className="apps-modern-setup__header">
              <div>
                <span className="apps-setup-eyebrow">App navigation</span>
                <h3>Train App Navigation</h3>
                <p>
                  Use the Google TV Remote controls above to position the app, then capture states,
                  train list focus, inspect route coverage, and test destinations here.
                </p>
              </div>
              <strong>
                Control transport: {controlTransport === 'google_tv'
                  ? 'Google TV Remote'
                  : 'Accessibility fallback'}
              </strong>
            </div>
            <PlaybookSetupPanel
              session={session}
              appLabel={label}
              disabled={false}
              controlTransport={controlTransport}
              onRemoteAction={dispatchRemoteAction}
              onMessage={setMessage}
              onError={setError}
            />
          </section>
          <PromptDetectionPanel
            session={session}
            packageName={app.package_name}
            appLabel={label}
            workingActions={GOOGLE_REMOTE_ACTIONS}
            disabled={false}
            simple
            controlTransport={controlTransport}
            onRemoteAction={dispatchRemoteAction}
            captureRequest={captureRequest}
            onRulesChanged={async () => {
              const refreshed = await getExternalAppCalibration(session.id);
              if (refreshed) setSession(refreshed);
              setRulesVersion((value) => value + 1);
            }}
            onMessage={setMessage}
            onError={setError}
          />
          <LaunchRecipePanel
            key={rulesVersion}
            session={session}
            appLabel={label}
            channelTargets={targets}
            disabled={false}
            onRemoteAction={onRemoteAction}
            onMessage={setMessage}
            onError={setError}
          />
          <PlaybackHealthPanel
            key={`monitor-${rulesVersion}`}
            session={session}
            appLabel={label}
            disabled={false}
            simple
            onAddBlocker={() => {
              setCaptureRequest((value) => value + 1);
              window.requestAnimationFrame(() => {
                document.querySelector('.apps-prompt-panel')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              });
            }}
            onMessage={setMessage}
            onError={setError}
          />
        </>
      )}
      {message && <div className="apps-configurator-status">{message}</div>}
      {error && status !== 'error' && <div className="apps-configurator-status error">{error}</div>}
    </section>
  );
}

export default ModernAppSetup;
